import { CircleAlert, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { classes } from '../outils/classes';
import { Bouton, type Icone } from './Bouton';
import styles from './Etats.module.css';

export interface ProprietesSquelette {
  lignes?: number;
  /** Hauteur d'un bloc unique (tuile, graphique) au lieu de lignes de texte. */
  hauteurBloc?: number;
  libelle?: string;
  className?: string;
}

/** Chargement : formes grises, sans roue animée (charte). */
export function Squelette({ lignes = 3, hauteurBloc, libelle = 'Chargement en cours', className }: ProprietesSquelette) {
  return (
    <div className={classes(styles.squelette, className)} role="status" aria-live="polite" aria-busy="true">
      <span className="cc-masque">{libelle}</span>
      {hauteurBloc ? (
        <span className={styles.bloc} style={{ height: hauteurBloc }} aria-hidden />
      ) : (
        Array.from({ length: lignes }, (_, i) => <span key={i} className={styles.ligne} aria-hidden />)
      )}
    </div>
  );
}

export interface ProprietesEtatVide {
  titre: string;
  children?: ReactNode;
  action?: ReactNode;
  icone?: Icone;
  niveau?: 2 | 3;
  className?: string;
}

/** État vide : une phrase explicative et, si possible, une action. */
export function EtatVide({ titre, children, action, icone: IconeEtat = Inbox, niveau = 3, className }: ProprietesEtatVide) {
  const Titre = niveau === 2 ? 'h2' : 'h3';
  return (
    <div className={classes(styles.etat, className)}>
      <IconeEtat className={styles.icone} strokeWidth={1.75} aria-hidden />
      <Titre>{titre}</Titre>
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  );
}

export interface ProprietesEtatErreur {
  titre?: string;
  message: string;
  /** Cause technique lisible (code d'erreur de l'API). */
  cause?: string | null;
  onReessayer?: () => void;
  action?: ReactNode;
  niveau?: 2 | 3;
}

/** État d'erreur : message, cause et action de réessai. */
export function EtatErreur({ titre = 'Le chargement a échoué', message, cause, onReessayer, action, niveau = 3 }: ProprietesEtatErreur) {
  const Titre = niveau === 2 ? 'h2' : 'h3';
  return (
    <div className={classes(styles.etat, styles.etatErreur)} role="alert">
      <CircleAlert className={styles.icone} strokeWidth={1.75} aria-hidden />
      <Titre>{titre}</Titre>
      <p>{message}</p>
      {cause ? <p className={styles.cause}>Cause : {cause}</p> : null}
      {onReessayer ? (
        <Bouton variante="secondaire" onClick={onReessayer}>
          Réessayer
        </Bouton>
      ) : null}
      {action}
    </div>
  );
}
